'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { isAppBridge } from '@/app/helpers/appStorageMethod';
import { paymentService } from '@/app/service/payments';

enum PaymentStatus {
    CANCEL = '01',
    RETURN = '02',
    FAIL = '04',
    SUCCESS_DESKTOP_DEVICE = '0000',
    SUCCESS_MOBILE_DEVICE = '00',
}

/**
 * Listen for payment callback from native app via AppBridge
 * Native app intercepts joysam:// URL scheme and sends payment data via postMessage
 */
export default function PaymentCallbackListener() {
    const router = useRouter();

    useEffect(() => {
        if (!isAppBridge()) return;
        const handlePaymentCallback = async (event: any) => {
            try {
                let url = '';
                if (typeof event.detail === 'string') {
                    url = event.detail;
                } else if (event.detail && typeof event.detail === 'object') {
                    url = event.detail.normalizedUrl || event.detail.url || '';
                }

                if (!url) {
                    console.log('[PaymentCallbackListener] Empty URL in event detail');
                    return;
                }

                const urlObj = new URL(url.replace('joysam://', 'https://joysamconnect.com/'));
                const dataEncoded = urlObj.searchParams.get('data');

                if (!dataEncoded) {
                    return;
                }

                // Decode and parse payment data
                const dataDecoded = decodeURIComponent(dataEncoded);

                const paymentData = JSON.parse(dataDecoded) as { [key: string]: any };

                // Process payment callback
                const result = await processPaymentCallback(paymentData);

                // Navigate based on result
                if (result.success && result.orderId) {
                    console.log('[PaymentCallback] Payment successful, redirecting to order details');
                    router.push(`/mychannel/detailpayments/${result.orderId}?from=payment`);
                    return;
                }

                if (result.success) {
                    console.log('[PaymentCallback] Payment successful, redirecting to result page');
                    const queryParams = new URLSearchParams(result.data as any).toString();
                    router.push(`/inicisTest/order-result?platform=mobile&productName=${encodeURIComponent(result.productName || 'Product')}&${queryParams}`);
                    return;
                }

                // Failed - navigate to error page
                console.log('[PaymentCallback] Payment failed, redirecting to error page');
                const filteredData = Object.fromEntries(
                    Object.entries(paymentData).filter(([key]) => key !== 'authToken')
                );
                const queryParams = new URLSearchParams(filteredData).toString();
                router.push(`/inicisTest/order-result?platform=mobile&statusCode=${PaymentStatus.FAIL}&${queryParams}`);

            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : String(error);
                console.error('[PaymentCallback] Error processing payment:', error);
                router.push(`/inicisTest/order-result?platform=mobile&statusCode=${PaymentStatus.FAIL}&error=${encodeURIComponent(errorMsg)}`);
            }
        };

        window.addEventListener('paymentData', handlePaymentCallback as EventListener);
        return () => {
            window.removeEventListener('paymentData', handlePaymentCallback as EventListener);
        };
    }, [router]);

    // This component doesn't render anything
    return null;
}

/**
 * Process payment callback data
 * Verify with backend and return result
 */
async function processPaymentCallback(paymentData: { [key: string]: any }): Promise<{
    success: boolean;
    orderId?: string;
    productName?: string;
    data?: any;
}> {
    try {
        // If already verified on the server-side, skip client-side API verification
        if (paymentData.orderId && paymentData.verificationResult) {
            console.log('[PaymentCallback] Already verified on server, orderId:', paymentData.orderId);
            const orderId = paymentData.orderId;
            const verificationResult = paymentData.verificationResult;
            const productName = verificationResult?.orderProducts?.[0]?.productName ||
                verificationResult?.order_products?.[0]?.product_name;
            return {
                success: true,
                orderId,
                productName,
                data: verificationResult
            };
        }

        // Check payment status (mobile uses P_STATUS)
        const isSuccess = paymentData.P_STATUS === PaymentStatus.SUCCESS_MOBILE_DEVICE;

        if (!isSuccess) {
            console.log('[PaymentCallback] Payment not successful, status:', paymentData.P_STATUS);
            return {
                success: false,
                data: paymentData
            };
        }

        // Verify with backend
        console.log('[PaymentCallback] Payment status success, verifying with backend...');
        let verificationResult;
        try {
            const response = await paymentService.postVerification({
                data: { ...paymentData, platform: 'mobile' },
            });
            verificationResult = response.data;
        } catch (error) {
            console.error('[PaymentCallback] Verification failed:', error);
            return {
                success: false,
                data: paymentData
            };
        }

        // Check verification result
        const verificationSuccess = verificationResult?.resultCode === PaymentStatus.SUCCESS_MOBILE_DEVICE;

        if (!verificationSuccess) {
            console.log('[PaymentCallback] Verification failed, resultCode:', verificationResult?.resultCode);
            return {
                success: false,
                data: paymentData
            };
        }

        // Extract order info
        const orderId = (verificationResult as any)?.orderId || (verificationResult as any)?.order_id;
        const productName = (verificationResult as any)?.orderProducts?.[0]?.productName ||
            (verificationResult as any)?.order_products?.[0]?.product_name;

        console.log('[PaymentCallback] Payment verified successfully, orderId:', orderId);

        return {
            success: true,
            orderId,
            productName,
            data: verificationResult
        };
    } catch (error) {
        console.error('[PaymentCallback] Error in processPaymentCallback:', error);
        throw error;
    }
}
